import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Calendar, ArrowLeft, Share2, Facebook, Twitter, Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function PressReleaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRelease();
  }, [id]);

  const fetchRelease = async () => {
    try {
      const response = await axios.get(`${API}/api/press-releases/${id}`);
      setRelease(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching press release:", error);
      toast.error("Press release not found");
      navigate("/press-releases");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const shareRelease = (platform) => {
    const url = window.location.href;
    const title = release?.title || '';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading press release...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!release) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Back Button */}
      <div className="max-w-5xl mx-auto px-4 pt-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/press-releases')}
          className="text-gray-600 hover:text-blue-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Press Releases
        </Button>
      </div>

      {/* Press Release Content */}
      <article className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-blue-600 text-white rounded-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="bg-white/20 px-4 py-2 rounded-full text-sm font-semibold">
              FOR IMMEDIATE RELEASE
            </span>
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>{formatDate(release.created_at)}</span>
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
            {release.title}
          </h1>

          {/* Share Buttons */}
          <div className="flex items-center space-x-3 pt-4 border-t border-white/20">
            <span className="text-blue-100 text-sm mr-2">Share:</span>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => shareRelease('facebook')}
              className="hover:bg-white/20 text-white"
            >
              <Facebook className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => shareRelease('twitter')}
              className="hover:bg-white/20 text-white"
            >
              <Twitter className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => shareRelease('linkedin')}
              className="hover:bg-white/20 text-white"
            >
              <Linkedin className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={copyLink}
              className="hover:bg-white/20 text-white"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none mb-12 bg-white rounded-lg p-8 shadow-sm"
          style={{
            lineHeight: '1.9',
            fontSize: '18px'
          }}
          dangerouslySetInnerHTML={{ __html: release.content.replace(/\n/g, '<br/><br/>') }}
        />

        {/* Media Contact Info */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 border-2 border-blue-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Media Contact Information</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Company Information</h4>
              <div className="space-y-2 text-gray-700">
                <p className="font-bold text-lg text-blue-600">Its-Sold.com™</p>
                <p className="text-sm text-gray-600">America's Original FSBO Marketplace - Since 1998</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Contact Details</h4>
              <div className="space-y-2 text-gray-700">
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-blue-600" />
                  <span>(877) 394-0210</span>
                </div>
                <div className="flex items-start">
                  <MapPin className="w-4 h-4 mr-2 text-blue-600 mt-1" />
                  <span>5624 Dyer St. Suite 601<br/>Dallas, TX 75206</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-blue-600" />
                  <a href="https://its-sold.com" className="text-blue-600 hover:underline">
                    its-sold.com
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-blue-200">
            <p className="text-sm text-gray-600 italic">
              For media inquiries, interviews, or additional information, please contact the press office at the number above.
            </p>
          </div>
        </Card>

        {/* About Section */}
        <Card className="bg-gray-50 p-8 mt-8 border-l-4 border-blue-600">
          <h3 className="text-xl font-bold text-gray-900 mb-4">About Its-Sold.com™</h3>
          <p className="text-gray-700 leading-relaxed">
            Its-Sold.com™ is America's Original For Sale By Owner (FSBO) marketplace, established in 1998. 
            The platform helps homeowners in the Dallas-Fort Worth area and beyond save thousands of dollars 
            in real estate commissions by providing professional tools and resources for selling homes independently. 
            With 27 years of experience, Its-Sold.com™ has helped thousands of homeowners successfully sell their 
            properties while maintaining 100% equity.
          </p>
        </Card>
      </article>

      {/* Bottom CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 py-12 mt-20">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Save Thousands?</h2>
          <p className="text-xl text-blue-100 mb-8">
            List your home FREE on Its-Sold.com™ and keep 100% of your equity
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6"
          >
            Get Started - It's FREE
          </Button>
        </div>
      </div>
    </div>
  );
}
